import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { api, apiError, CATEGORIES } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const blank = {
  name: "", category: CATEGORIES[0], price: "", description: "",
  calories: "", image: "", visible: true, signature: false, order: 0,
};

export const MenuManager = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/admin/menu");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item, calories: item.calories ?? "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      calories: form.calories === "" ? null : Number(form.calories),
      order: Number(form.order) || 0,
    };
    delete payload.id;
    try {
      if (editing) await api.put(`/admin/menu/${editing.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const toggleVisible = async (item) => {
    const { id, ...rest } = item;
    await api.put(`/admin/menu/${item.id}`, { ...rest, visible: !item.visible });
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    await api.delete(`/admin/menu/${item.id}`);
    toast.success("Item deleted");
    load();
  };

  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="admin-menu-manager">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-cream">Menu Items <span className="text-sm text-muted-foreground">({items.length})</span></h2>
        <Button onClick={openNew} data-testid="admin-add-item-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel">
          <Plus className="mr-1.5 h-4 w-4" /> Add Item
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {["All", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setFilter(c)}
            data-testid={`admin-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
              filter === c ? "border-copper bg-copper text-charcoal" : "border-border text-cream/70 hover:text-caramel"
            }`}>
            {c}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-2">
        {shown.map((item) => (
          <div key={item.id} data-testid={`admin-menu-row-${item.id}`}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3">
            {item.image ? (
              <img src={item.image} alt="" className="h-12 w-12 rounded-lg object-cover" />
            ) : <div className="h-12 w-12 rounded-lg bg-muted" />}
            <div className="min-w-[160px] flex-1">
              <p className="text-sm font-semibold text-cream">{item.name}</p>
              <p className="text-xs text-muted-foreground">
                {item.category} · {item.calories ?? "—"} kcal {item.signature ? "· Signature" : ""}
              </p>
            </div>
            <span className="text-sm text-caramel">{item.price ? `₹ ${item.price}` : "₹ Price"}</span>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => toggleVisible(item)}
                data-testid={`admin-toggle-visible-${item.id}`} title={item.visible ? "Hide" : "Show"}>
                {item.visible ? <Eye className="h-4 w-4 text-caramel" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
              </Button>
              <Button size="icon" variant="ghost" onClick={() => openEdit(item)} data-testid={`admin-edit-item-${item.id}`}>
                <Pencil className="h-4 w-4 text-cream/80" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => remove(item)} data-testid={`admin-delete-item-${item.id}`}>
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editing ? "Edit Item" : "Add Item"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} data-testid="item-name-input" required
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹)</Label>
                <Input value={form.price} data-testid="item-price-input" placeholder="leave blank for ₹ Price"
                  onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Calories</Label>
                <Input value={form.calories} data-testid="item-calories-input" type="number"
                  onChange={(e) => setForm({ ...form, calories: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} data-testid="item-description-input" rows={2}
                onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input value={form.image} data-testid="item-image-input"
                onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1.5" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm text-cream">Visible on website</span>
              <Switch checked={form.visible} data-testid="item-visible-switch"
                onCheckedChange={(v) => setForm({ ...form, visible: v })} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm text-cream">Signature item</span>
              <Switch checked={form.signature} data-testid="item-signature-switch"
                onCheckedChange={(v) => setForm({ ...form, signature: v })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="item-save-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel">
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
